// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The six-bus mixer and the master limiter (M3-20, 07 §2 and §7).
//
// Six buses — `mechanical`, `game`, `speech`, `music`, `ambience`, `ui` — because a coil firing
// and a jackpot fanfare want completely different dynamics treatment, and because every mixing
// rule in 07 §7 is written as "this bus, that many dB". A single output with per-sound gains
// cannot express "duck the music under speech" at all; it can only turn each piece of music
// down and hope they were all playing.
//
// Each bus is four nodes, and the **order is the decision**:
//
//     input ── compressor ── volume ── duck ──┐
//                                             ├── master ── limiter ── out
//     (five more buses)  ────────────────────┘
//
//   * **`input` is a plain summing point** at unity. Voices connect here and nowhere else, so a
//     voice never needs to know what a bus is made of.
//   * **The compressor comes before the volume**, which looks backwards and is not. A compressor
//     has a fixed threshold, so putting it after the slider means the bus's *character* changes
//     as the player moves the slider: quiet settings stop compressing and the dynamics open up.
//     Before the slider, the bus always sounds like itself and the slider is a clean trim.
//   * **`duck` is separate from `volume`** so automation and the player are not writing to the
//     same parameter. Sharing one gain means a callout that ducks the music to −8 dB and then
//     restores it to unity has just undone whatever the player set, and there is no way to tell
//     the two intentions apart after the fact.
//
// The limiter is a compressor at ratio 20 with a 3 ms attack, which is what Web Audio gives you
// in place of a real limiter. It exists because a machine at full multiball genuinely produces
// peaks that clip: six balls, three bumpers and a knocker is not a mix that behaves.
//
// **Nothing here makes a sound.** It is the graph the sounds arrive at, so it can be built,
// asserted and torn down without a single oscillator — and, more to the point, without a
// browser.

import {
  decibelsToGain,
  type AudioGraphContext,
  type AudioNodeLike,
  type CompressorNodeLike,
  type GainNodeLike,
} from './webAudio'

export const BUS_NAMES = ['mechanical', 'game', 'speech', 'music', 'ambience', 'ui'] as const
export type BusName = (typeof BUS_NAMES)[number]

export interface CompressorSettings {
  /** dBFS above which reduction begins. */
  readonly threshold: number
  /** dB of soft knee around the threshold. */
  readonly knee: number
  readonly ratio: number
  /** Seconds. */
  readonly attack: number
  readonly release: number
}

/**
 * Dynamics per bus, and they differ because the material differs.
 *
 * A coil transient is two milliseconds long: a slow attack lets the whole thing past and then
 * pumps the tail, which is audibly worse than not compressing at all. Music is the opposite —
 * catch it fast and it breathes on every kick.
 */
export const BUS_COMPRESSION: Readonly<Record<BusName, CompressorSettings>> = {
  // Fast and firm. Solenoids are all transient, and this bus is what stops a bumper storm
  // eating the master limiter's entire headroom.
  mechanical: { threshold: -16, knee: 4, ratio: 4, attack: 0.002, release: 0.09 },
  // Game tones are already synthesised at a controlled level; this is a safety net.
  game: { threshold: -12, knee: 6, ratio: 3, attack: 0.004, release: 0.12 },
  // Speech is the one bus that must stay intelligible under everything else, so it is
  // compressed hardest — a level, forward voice rather than a natural one.
  speech: { threshold: -20, knee: 3, ratio: 6, attack: 0.003, release: 0.18 },
  // Slow, gentle, musical. Anything faster and the pad pumps against the kick.
  music: { threshold: -14, knee: 8, ratio: 2.5, attack: 0.012, release: 0.25 },
  // Barely touched: ambience is quiet by construction and compressing it just raises the
  // noise floor, which is the one thing a room bed must not do.
  ambience: { threshold: -24, knee: 8, ratio: 2, attack: 0.05, release: 0.4 },
  ui: { threshold: -12, knee: 4, ratio: 3, attack: 0.003, release: 0.1 },
}

/**
 * Where each bus sits by default, dB. Speech slightly forward, ambience well back.
 *
 * **The machine is 6 dB up on both clients, which is exactly twice as loud** (M8-34). Reported from
 * a Quest on 31 Aug 2026: "the machine sounds (bumpers, flippers, balls, etc.) are too low when on
 * full volume ... double the volume of the machine sounds." 6.0206 dB is a factor of two in
 * amplitude, and 6 is what the rest of this file's numbers are rounded to.
 *
 * **A trim rather than the compressor**, because this gain is downstream of the bus dynamics — see
 * `audioPrefs.ts`, "the compressor is upstream of this gain precisely so it cannot" change a bus's
 * character.
 *
 * **The headroom is measured, not argued** — `mix-graph-parity.test.ts` owns it now, and it caught
 * this comment being wrong. Summing peaks the way the mixer does, from the product's own cue
 * gains: six simultaneous `scoopEject`-level coils leave `mechanical` at −3.1 dBFS, comfortably
 * under the −1 dBFS limiter, because that bus's fast, firm compressor is exactly what its own
 * comment says it is for. `game` is the tighter one and always was — its compressor is *gentler*
 * (−12 dBFS, ratio 3), so at any given input it leaves about 4 dB hotter than `mechanical`, and
 * three chime bars plus a jackpot stab land at −2.2 dBFS. Tight, and it does not cross.
 *
 * `ui` stays where it is: a menu click is not a machine sound, whatever group it mutes with. Speech,
 * music and ambience keep their absolute levels and now sit 6 dB below the machine; every rule in
 * 07 §7 is a *relative* move, so all of them still say what they said.
 */
export const BUS_TRIM_DB: Readonly<Record<BusName, number>> = {
  mechanical: 6,
  game: 5,
  speech: 1.5,
  music: -3,
  ambience: -9,
  ui: -4,
}

/**
 * The master limiter: −1 dBFS, per 07 §9.
 *
 * Ratio 20 is Web Audio's ceiling and the conventional stand-in for a limiter. The 3 ms attack
 * lets the very first sample of a transient through — a true zero-attack limiter would need
 * lookahead, which `DynamicsCompressorNode` does not have — and 3 ms of a coil transient
 * passing at full scale is inaudible against the 250 ms release that follows it.
 */
export const MASTER_LIMITER: CompressorSettings = {
  threshold: -1,
  knee: 0,
  ratio: 20,
  attack: 0.003,
  release: 0.25,
}

/**
 * Seconds for a volume or duck move to reach about 63% of its target.
 *
 * Exported because it is part of the mix table `mix-graph-parity.test.ts` holds the Quest to, not
 * because anything outside this module sets a gain. `MixGraph.VolumeGlide` is the same number.
 */
export const VOLUME_GLIDE = 0.02

/**
 * Seconds for the master to fall away when the machine is paused (M3-55).
 *
 * Longer than `VOLUME_GLIDE` because this one is *heard as a gesture* rather than as a slider
 * settling: 20 ms on a callout mid-word is a click, which is the artefact the fade exists to
 * remove. Short enough that the pause still feels instant — the ear reads anything under about
 * a tenth of a second as immediate, and a pause that audibly takes its time feels like lag.
 */
export const PAUSE_FADE = 0.06

/**
 * How long to wait before suspending the clock, milliseconds.
 *
 * `setTargetAtTime` is exponential and never truly arrives, so suspending after one time
 * constant would freeze the master at about 37% — audible, and frozen there for the whole pause.
 * Four constants is down about 32 dB, which is silence for this purpose, plus a little slack so
 * the timer cannot beat the audio thread to it.
 */
const PAUSE_FADE_MS = PAUSE_FADE * 4 * 1000 + 20

/**
 * The most a mixing rule may *raise* a bus, dB.
 *
 * 07 §7's largest boost is +2. The bound is not about that rule — it is about the next one: an
 * automation gain with no ceiling turns a typo into a bus that swamps the limiter, and the
 * limiter is the only thing between the mix and a clipped machine.
 */
export const MAX_OFFSET_DB = 12

/**
 * One bus: a place to connect, a volume the player owns and a duck the mixing rules own.
 */
export class MixerBus {
  readonly name: BusName
  /** Connect a voice here. The only node outside this class that anything should touch. */
  readonly input: GainNodeLike

  private readonly compressor: CompressorNodeLike
  private readonly volumeGain: GainNodeLike
  private readonly duckGain: GainNodeLike
  private readonly context: AudioGraphContext

  private level = 1
  private ducked = 1

  constructor(context: AudioGraphContext, name: BusName, destination: AudioNodeLike) {
    this.context = context
    this.name = name

    this.input = context.createGain()
    this.input.gain.value = 1

    this.compressor = context.createDynamicsCompressor()
    applyCompression(this.compressor, BUS_COMPRESSION[name])

    this.volumeGain = context.createGain()
    this.volumeGain.gain.value = decibelsToGain(BUS_TRIM_DB[name])

    this.duckGain = context.createGain()
    this.duckGain.gain.value = 1

    this.input.connect(this.compressor)
    this.compressor.connect(this.volumeGain)
    this.volumeGain.connect(this.duckGain)
    this.duckGain.connect(destination)
  }

  /** The player's slider, 0 to 1, on top of the bus's own trim. */
  get volume(): number {
    return this.level
  }

  /**
   * Set the player's level.
   *
   * Glided rather than assigned. A gain jumped between two values mid-signal is a step
   * discontinuity, which is a click — audible on every slider drag, and the reason a settings
   * panel that writes `gain.value` sixty times a second sounds broken.
   */
  setVolume(level: number, at = this.context.currentTime): void {
    this.level = clamp01(level)
    const target = this.level * decibelsToGain(BUS_TRIM_DB[this.name])
    this.volumeGain.gain.setTargetAtTime(target, at, VOLUME_GLIDE)
  }

  /** What the mixing rules have done to this bus, 1 being untouched. */
  get offsetLevel(): number {
    return this.ducked
  }

  /**
   * Move this bus by a number of dB, or back to 0 to release. `MixRules` decides when; this is
   * only how.
   *
   * **An offset rather than a duck, because one of 07 §7's rules goes the other way.** This
   * clamped to zero on the reasoning that "no mixing rule in 07 §7 ever intends" a boost, and
   * the multiball row says `mechanical` +2 dB — so the clamp was quietly discarding a rule the
   * document asks for. It is bounded above instead, at a level a mistyped rule cannot use to
   * blow the mix past the limiter.
   *
   * The caller supplies the time constant because the ear is not symmetrical about it: a duck
   * has to arrive fast enough that the first syllable is already clear, and leave slowly enough
   * that the music does not lurch back in.
   */
  offset(db: number, timeConstant: number, at = this.context.currentTime): void {
    this.ducked = decibelsToGain(Math.min(db, MAX_OFFSET_DB))
    this.duckGain.gain.setTargetAtTime(this.ducked, at, timeConstant)
  }

  /** Silence this bus immediately — the blackout rule, and teardown. */
  silence(at = this.context.currentTime): void {
    this.ducked = 0
    this.duckGain.gain.cancelScheduledValues(at)
    this.duckGain.gain.setValueAtTime(0, at)
  }

  dispose(): void {
    this.input.disconnect()
    this.compressor.disconnect()
    this.volumeGain.disconnect()
    this.duckGain.disconnect()
  }
}

export class Mixer {
  readonly context: AudioGraphContext
  readonly buses: Readonly<Record<BusName, MixerBus>>

  private readonly masterGain: GainNodeLike
  private readonly limiter: CompressorNodeLike
  private level = 1
  private muted = false
  /** The machine is paused (M3-55). A third, independent reason for the master to be down. */
  private paused = false

  constructor(context: AudioGraphContext, destination: AudioNodeLike = context.destination) {
    this.context = context

    // Built output-first, so every bus has somewhere to connect the moment it exists and there
    // is no window in which a bus is dangling.
    this.limiter = context.createDynamicsCompressor()
    applyCompression(this.limiter, MASTER_LIMITER)
    this.limiter.connect(destination)

    this.masterGain = context.createGain()
    this.masterGain.gain.value = 1
    this.masterGain.connect(this.limiter)

    const buses = {} as Record<BusName, MixerBus>
    for (const name of BUS_NAMES) buses[name] = new MixerBus(context, name, this.masterGain)
    this.buses = buses
  }

  bus(name: BusName): MixerBus {
    return this.buses[name]
  }

  get masterVolume(): number {
    return this.level
  }

  setMasterVolume(level: number, at = this.context.currentTime): void {
    this.level = clamp01(level)
    this.applyMaster(at)
  }

  get isMuted(): boolean {
    return this.muted
  }

  /**
   * Mute everything without losing where the sliders were.
   *
   * A mute implemented by writing zero into the master volume is a mute that forgets: unmuting
   * restores whatever the code guessed rather than what the player chose, and the guess is
   * always 1.
   */
  setMuted(muted: boolean, at = this.context.currentTime): void {
    this.muted = muted
    this.applyMaster(at)
  }

  /**
   * Fade the whole mix out for a pause, or back in for a resume (M3-55).
   *
   * **A third input to the master, not a write to it.** The same argument `setMuted` makes: a
   * pause that wrote zero into `level` would resume to whatever the code guessed rather than
   * where the player left the slider, and a pause that wrote zero *while muted* would unmute the
   * machine on resume. Folding all three into `applyMaster` means they compose in any order and
   * any combination — muted-then-paused-then-unmuted lands exactly where it should.
   *
   * Slower than a slider move on the way down, so a callout caught mid-word ends on a fall
   * instead of a click. Immediate on the way back, because the clock has to be running before
   * this can be heard at all and the resume is already the slow part.
   */
  setPaused(paused: boolean, at = this.context.currentTime): void {
    this.paused = paused
    this.masterGain.gain.setTargetAtTime(
      this.gainNow(),
      at,
      paused ? PAUSE_FADE : VOLUME_GLIDE,
    )
  }

  /** How long a pause fade needs before the clock may stop, milliseconds. */
  static get pauseFadeMs(): number {
    return PAUSE_FADE_MS
  }

  /**
   * Gain reduction the limiter is applying, dB and negative. Zero means it is not working.
   *
   * **This has no reader on this client, and saying so is the point** (M8-36). It used to say
   * "read for metering", which is the kind of sentence that makes a feature look wired: the Quest's
   * three equivalents all carried one, all were correct, and none of them had a reader either —
   * so when M8-34 doubled the machine's volume, "does the mix now hold the limiter down" was
   * answered with arithmetic while four instruments sat unread.
   *
   * The Quest wired its own, into `MachineAudio.Describe`, because a headset has no console and its
   * heartbeat is the only place a number can be read off a device.
   *
   * **The harness HUD reads it now** (M9-55), which is the "first browser diagnostic surface" the
   * previous version of this comment was waiting for — and that wait is why it was worth checking
   * rather than assuming: a member sweep listed this as a value nobody read, and the sentence
   * above it was already the answer. *Read the comment before calling something a defect*, which
   * is the same discipline as grepping for a reader before denying one.
   *
   * **A test still cannot stand in for it.** `recordingContext.ts`'s compressor double reports
   * `reduction = 0` always, so an assertion on this number would pass against a constant. Where
   * the claim is discharged is `mix-graph-parity.test.ts`, which asserts statically that no bus,
   * at the loudest input a voice can carry, presents enough to cross this limiter — the half that
   * can be settled without a device. The HUD line is the other half: a person, listening, with the
   * number in front of them.
   */
  get limiting(): number {
    return this.limiter.reduction
  }

  dispose(): void {
    for (const name of BUS_NAMES) this.buses[name].dispose()
    this.masterGain.disconnect()
    this.limiter.disconnect()
  }

  /**
   * The master gain the three inputs currently add up to.
   *
   * One definition, read by both `applyMaster` and `setPaused`, because the alternative is two
   * expressions that have to be kept agreeing — and the one that gets forgotten is whichever is
   * touched less, which is how a pause taken while muted comes back at full volume.
   */
  private gainNow(): number {
    return this.muted || this.paused ? 0 : this.level
  }

  private applyMaster(at: number): void {
    this.masterGain.gain.setTargetAtTime(this.gainNow(), at, VOLUME_GLIDE)
  }
}

function applyCompression(node: CompressorNodeLike, settings: CompressorSettings): void {
  node.threshold.value = settings.threshold
  node.knee.value = settings.knee
  node.ratio.value = settings.ratio
  node.attack.value = settings.attack
  node.release.value = settings.release
}

function clamp01(value: number): number {
  return Number.isFinite(value) ? Math.min(Math.max(value, 0), 1) : 0
}
